import { PaginatedResponse, useApi } from '@/api';
import { ApplyButton, Page, PageSkeleton, TableCellBottom } from '@/components';
import { useSearchParams } from '@/hooks';
import {
  ArrowUpRightBoxIcon,
  CryptoIcon,
  CurrencyValue,
  DataTable,
  Flex,
  Heading,
  Input,
  TableSkeleton,
  Text,
  TextSearchFilter,
  useTableProps,
  WalletAddress
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

type AirdropData = {
  wallet_address: string;
  token_reward_s1: string | null;
  token_reward_s1_usd: string | null;
  token_reward_s2: string | null;
  token_reward_s2_usd: string | null;
  token_reward_total: string | null;
  token_reward_total_usd: string | null;
};

export default function AirdropLeaderboardPage() {
  const { network } = useParams() as {
    network: string;
  };
  const { pagination, sorting, filters } = useTableProps({
    pageSize: 15,
    initialSorting: '-token_reward_total',
    filtersDebounceTime: 500,
    initialFilters: {
      textSearch: ''
    }
  });
  const [marketCapParam, setMarketCapParam] = useSearchParams('marketCap', '');
  const [marketCap, setMarketCap] = useState<string>(marketCapParam ?? '');
  const [isFormUpdated, setIsFormUpdated] = useState(false);

  const onCalculate = () => {
    setMarketCapParam(marketCap);
    setIsFormUpdated(false);
  };

  const handleAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (/^\d*$/.test(value)) {
        setMarketCap(value);
        setIsFormUpdated(true);
      }
    },
    []
  );

  const [data, error] = useApi<PaginatedResponse<AirdropData[]>>({
    url: '/sparkstar/spk/airdrop/',
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      spk_value: (Number(marketCapParam) / 10_000_000_000).toString(),
      search: filters.filtersApplied['textSearch']
    }
  });
  const columns: TableColumn[] = useMemo(
    () => [
      {
        header: 'Wallet',
        getCell: (row) => <WalletAddress address={row.wallet_address} />,
        sticky: true
      },
      {
        header: 'SparkLend Season 1',
        getCell: (row) =>
          row.token_reward_s1 > 0 ? (
            <CurrencyValue value={row.token_reward_s1} />
          ) : (
            '-'
          ),
        getCellBottom: (row) => (
          <TableCellBottom checkValue={row.token_reward_s1_usd}>
            <CurrencyValue
              value={row.token_reward_s1_usd}
              options={{
                currency: 'USD'
              }}
            />
          </TableCellBottom>
        ),
        sortingField: 'token_reward_s1',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'SparkLend Season 2',
        getCell: (row) =>
          row.token_reward_s2 > 0 ? (
            <CurrencyValue value={row.token_reward_s2} />
          ) : (
            '-'
          ),
        getCellBottom: (row) => (
          <TableCellBottom checkValue={row.token_reward_s2_usd}>
            <CurrencyValue
              value={row.token_reward_s2_usd}
              options={{
                currency: 'USD'
              }}
            />
          </TableCellBottom>
        ),
        sortingField: 'token_reward_s2',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Aave',
        getCell: (row) =>
          row.token_reward_aave > 0 ? (
            <CurrencyValue value={row.token_reward_aave} />
          ) : (
            '-'
          ),
        getCellBottom: (row) => (
          <TableCellBottom checkValue={row.token_reward_aave_usd}>
            <CurrencyValue
              value={row.token_reward_aave_usd}
              options={{
                currency: 'USD'
              }}
            />
          </TableCellBottom>
        ),
        sortingField: 'token_reward_aave',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Total',
        getCell: (row) => <CurrencyValue value={row.token_reward_total} />,
        getCellBottom: (row) => (
          <TableCellBottom checkValue={row.token_reward_total_usd}>
            <CurrencyValue
              value={row.token_reward_total_usd}
              options={{
                currency: 'USD'
              }}
            />
          </TableCellBottom>
        ),
        sortingField: 'token_reward_total',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ],
    []
  );
  if (error) {
    throw error;
  }
  if (!data) {
    return (
      <PageSkeleton>
        <TableSkeleton rows={25} columns={4} />
      </PageSkeleton>
    );
  }

  return (
    <Page
      title={
        <Flex flexDirection="row" gap="2xs" alignItems="center">
          <CryptoIcon
            name="SPK"
            address="0x00000000000000000000000000000000000spark"
            network={network}
            size="4"
          />
          <Heading>Pre-Farming Airdrop</Heading>
        </Flex>
      }>
      <Text color="muted" fontSize="-1">
        The first season of the Spark Pre-Farming Airdrop concluded with block
        number 19911718, marking the start of Season 2 with the subsequent
        block. Final token supply had been increased from 4.6b to 10b and all
        concluded and ongoing token rewards were proportionally increased. In
        Season 2, a total of 14.4782 million tokens will be emitted monthly,
        distributed equally across each block. For more information, please
        visit the{' '}
        <Flex
          as="a"
          display="inline-flex"
          gap="3xs"
          alignItems="center"
          // @ts-ignore
          href="https://docs.spark.fi/governance/spk-token"
          target="_blank"
          rel="noreferrer">
          SPK Token & Airdrop FAQ page
          <ArrowUpRightBoxIcon size="-1" />
        </Flex>
        .
      </Text>

      <Flex flexDirection="column" gap="3xs">
        <Flex gap="3xs" color="muted" fontSize="-1">
          Market cap:
          <CurrencyValue
            value={Number(marketCap) || 0}
            options={{ currency: 'USD' }}
          />
        </Flex>

        <Flex
          flexDirection={['column', 'row']}
          gap="m"
          justifyContent="space-between"
          flexWrap="wrap">
          <Flex
            flexDirection={['column', 'row']}
            gap="xs"
            alignItems={['flex-start', 'center']}>
            <Flex flexDirection="column" gap="3xs">
              <Flex gap="s">
                <Input
                  type="text"
                  value={marketCap}
                  onChange={handleAmountChange}
                  placeholder="SPK market cap in $"
                />
                <ApplyButton
                  // @ts-ignore
                  type="submit"
                  disabled={
                    !isFormUpdated ||
                    isNaN(Number(marketCap)) ||
                    Number(marketCap) <= 0
                  }
                  onClick={onCalculate}>
                  Calculate
                </ApplyButton>
              </Flex>
            </Flex>
            <Flex fontSize="-1" color="muted">
              SPK token valuation:
              <CurrencyValue
                value={Number(marketCapParam) / 10_000_000_000}
                options={{
                  currency: 'USD',
                  formatCurrencyOptions: {
                    maximumFractionDigits: 3
                  }
                }}
              />
            </Flex>
          </Flex>
          <TextSearchFilter
            pagination={pagination}
            placeholder="Search by wallet"
            display="flex"
            flex="1"
            options={{
              variants: {
                textSearchFilter: 'fullWidth'
              }
            }}
            {...filters}
          />
        </Flex>
      </Flex>

      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => row.wallet_address}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
      <Flex flexDirection="column" gap="3xs" justifyContent="flex-end">
        <Text color="muted" fontSize="-1">
          * we are only showing wallets with more than 1 SPK reward
        </Text>
      </Flex>
    </Page>
  );
}
